import { useRef, useCallback } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Download, Mic, Calendar } from 'lucide-react';
import html2canvas from 'html2canvas';
import ProgressButton from './components/ProgressButton';
import FloatingMascot from './components/FloatingMascot';

interface ShareData {
  totalMessages: number;
  weeklyStats: {
    messages: number;
    daysActive: number;
    estimatedMinutes: number;
  };
  estimatedMinutesTotal: number;
}

interface ShareProps {
  data: ShareData;
  userName: string;
}

// Mini tetris piece for decoration
function MiniTetrisPiece({ color = 'purple', delay = 0 }: { color?: 'purple' | 'green' | 'blue'; delay?: number }) {
  const colors = {
    purple: 'from-purple-500 to-purple-600',
    green: 'from-green-400 to-green-500',
    blue: 'from-blue-400 to-blue-500',
  };

  return (
    <motion.div
      className="flex gap-0.5"
      initial={{ opacity: 0, scale: 0 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ delay, type: 'spring', stiffness: 200 }}
    >
      <div className={`w-2 h-2 rounded-sm bg-gradient-to-br ${colors[color]} shadow-sm`} />
      <div className={`w-2 h-2 rounded-sm bg-gradient-to-br ${colors[color]} shadow-sm`} />
    </motion.div>
  );
}

// Achievement block - tetris-style filled block indicator
function AchievementBlock({ achievement, index }: { achievement: string; index: number }) {
  const blockColors = [
    { bg: 'from-purple-500/80 to-purple-600/80', glow: 'shadow-purple-500/40' },
    { bg: 'from-green-400/80 to-green-500/80', glow: 'shadow-green-500/40' },
    { bg: 'from-blue-400/80 to-blue-500/80', glow: 'shadow-blue-500/40' },
  ];
  const colorSet = blockColors[index % blockColors.length];

  return (
    <motion.div
      className="flex items-center gap-3 px-4 py-3 border border-white/10 rounded-xl"
      style={{ backgroundColor: 'rgba(255, 255, 255, 0.05)' }}
      initial={{ opacity: 0, x: -20 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ delay: 0.9 + index * 0.1, type: 'spring' }}
      whileHover={{ scale: 1.02, backgroundColor: 'rgba(255,255,255,0.08)' }}
    >
      {/* Tetris block indicator */}
      <motion.div
        className="flex flex-col gap-0.5"
        initial={{ scale: 0, rotate: -90 }}
        animate={{ scale: 1, rotate: 0 }}
        transition={{ delay: 1 + index * 0.1, type: 'spring', stiffness: 200 }}
      >
        <div className="flex gap-0.5">
          <div className={`w-2.5 h-2.5 rounded-sm bg-gradient-to-br ${colorSet.bg} shadow-md ${colorSet.glow}`} />
          <div className={`w-2.5 h-2.5 rounded-sm bg-gradient-to-br ${colorSet.bg} shadow-md ${colorSet.glow}`} />
        </div>
        <div className="flex gap-0.5">
          <div className={`w-2.5 h-2.5 rounded-sm bg-gradient-to-br ${colorSet.bg} shadow-md ${colorSet.glow}`} />
          <div className={`w-2.5 h-2.5 rounded-sm bg-gradient-to-br ${colorSet.bg} shadow-md ${colorSet.glow}`} />
        </div>
      </motion.div>

      <span className="text-sm flex-1" style={{ color: '#fff', fontWeight: 400 }}>{achievement}</span>

      {/* Subtle glow dot */}
      <motion.div
        className={`w-2 h-2 rounded-full bg-gradient-to-br ${colorSet.bg}`}
        animate={{ scale: [1, 1.3, 1], opacity: [0.5, 1, 0.5] }}
        transition={{ duration: 2, repeat: Infinity, delay: index * 0.3 }}
      />
    </motion.div>
  );
}

// Determine achievements based on real data (same logic as Progress)
function getAchievements(totalMessages: number): string[] {
  const achievements: string[] = [];
  if (totalMessages >= 3) achievements.push('Se apresentar em inglês');
  if (totalMessages >= 15) achievements.push('Falar sobre sua rotina');
  if (totalMessages >= 30) achievements.push('Manter uma conversa simples');
  if (totalMessages >= 50) achievements.push('Expressar opiniões em inglês');
  if (totalMessages >= 100) achievements.push('Conversar sobre temas variados');
  return achievements.length > 0 ? achievements : ['Começou a falar em inglês'];
}

function Share({ data, userName }: ShareProps) {
  const navigate = useNavigate();
  const cardRef = useRef<HTMLDivElement>(null);

  const achievements = getAchievements(data.totalMessages);
  const weekMinutes = data.weeklyStats.estimatedMinutes;
  const weekDays = data.weeklyStats.daysActive;

  const handleDownload = useCallback(async () => {
    if (!cardRef.current) return;

    try {
      // Pequena pausa para garantir que o DOM e fontes estão pintados (imagem salva = exatamente o que aparece no app)
      await new Promise((r) => requestAnimationFrame(() => requestAnimationFrame(r)));

      const canvas = await html2canvas(cardRef.current, {
        backgroundColor: '#1a0a2e',
        scale: 2,
        useCORS: true,
        logging: false,
        onclone(_, clonedElement) {
          // Esconder os glows no clone para a imagem baixada = card sem círculos no meio
          clonedElement.querySelectorAll('.share-card-glow').forEach((el) => {
            (el as HTMLElement).style.display = 'none';
          });
        },
      });

      const link = document.createElement('a');
      link.download = `ataric-progresso-${userName.toLowerCase().replace(/\s+/g, '-')}.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
    } catch {
      alert('Para salvar a imagem, faça uma captura de tela (screenshot) do card.');
    }
  }, [userName]);

  return (
    <div className="share-page min-h-screen bg-gradient-to-b from-[#0f0518] via-[#1a0a2e] to-[#0f0518] flex flex-col">
      {/* Header */}
      <motion.header
        className="p-4 flex items-center gap-4"
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
      >
        <ProgressButton
          variant="icon"
          size="icon"
          onClick={() => navigate('/progresso')}
        >
          <ArrowLeft className="w-5 h-5" />
        </ProgressButton>
        <span className="text-sm" style={{ color: '#fff', fontWeight: 400 }}>Compartilhar</span>
      </motion.header>

      {/* Instagram Story Card - 9:16 aspect ratio */}
      <div className="flex-1 flex items-center justify-center p-4">
        <motion.div
          ref={cardRef}
          className="w-full max-w-sm aspect-[9/16] relative overflow-hidden rounded-3xl"
          style={{
            background: 'linear-gradient(180deg, #1a0a2e 0%, #0f0518 50%, #1a0a2e 100%)',
          }}
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.6, ease: 'easeOut' }}
        >
          {/* Subtle grid pattern */}
          <div
            className="absolute inset-0 opacity-10"
            style={{
              backgroundImage: `
                linear-gradient(rgba(168, 85, 247, 0.3) 1px, transparent 1px),
                linear-gradient(90deg, rgba(168, 85, 247, 0.3) 1px, transparent 1px)
              `,
              backgroundSize: '40px 40px',
            }}
          />

          {/* Glow effects (ocultos só na captura para o download = card sem círculos extras) */}
          <div className="share-card-glow absolute top-1/4 left-1/2 -translate-x-1/2 w-64 h-64 bg-purple-600/20 rounded-full blur-3xl" />
          <div className="share-card-glow absolute bottom-1/4 left-1/2 -translate-x-1/2 w-48 h-48 bg-purple-500/10 rounded-full blur-3xl" />

          {/* Content */}
          <div className="relative z-10 h-full flex flex-col items-center justify-between p-8 py-12">

            {/* Top - Logo + User Name */}
            <motion.div
              className="flex flex-col items-center gap-3"
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
            >
              <FloatingMascot size="md" />
              <div className="text-center">
                <p className="text-xs uppercase tracking-widest mb-1" style={{ color: '#fff', fontWeight: 400 }}>Progresso de</p>
                <span className="text-white text-2xl font-bold tracking-wide">{userName}</span>
              </div>
            </motion.div>

            {/* Center - Main Message */}
            <motion.div
              className="text-center space-y-4"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.4, duration: 0.6 }}
            >
              <div className="space-y-2">
                <motion.p
                  className="text-3xl font-bold text-white leading-tight"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.5 }}
                >
                  Eu falei inglês.
                </motion.p>
                <motion.p
                  className="text-xl"
                  style={{ color: '#fff', fontWeight: 400 }}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.6 }}
                >
                  Mesmo errando.
                </motion.p>
              </div>

              {/* Stats */}
              <motion.div
                className="flex justify-center gap-8 pt-4"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.7 }}
              >
                <div className="text-center">
                  <div className="flex items-center justify-center gap-1.5 mb-1">
                    <Mic className="w-4 h-4 text-white" />
                    <span className="text-2xl font-bold text-white">{weekMinutes}</span>
                  </div>
                  <p className="text-xs uppercase tracking-wider" style={{ color: '#fff', fontWeight: 400 }}>minutos</p>
                </div>
                <div className="w-px h-12 bg-white/10" />
                <div className="text-center">
                  <div className="flex items-center justify-center gap-1.5 mb-1">
                    <Calendar className="w-4 h-4 text-white" />
                    <span className="text-2xl font-bold text-white">{weekDays}</span>
                  </div>
                  <p className="text-xs uppercase tracking-wider" style={{ color: '#fff', fontWeight: 400 }}>dias</p>
                </div>
              </motion.div>
            </motion.div>

            {/* Bottom - Achievements */}
            <motion.div
              className="w-full space-y-4"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.8, duration: 0.6 }}
            >
              {/* Title with mini tetris pieces */}
              <div className="flex items-center justify-center gap-3">
                <div className="h-px flex-1 bg-gradient-to-r from-transparent to-purple-500/50" />
                <div className="flex items-center gap-3 text-white">
                  <MiniTetrisPiece color="purple" delay={0.85} />
                  <span className="text-sm uppercase tracking-widest" style={{ color: '#fff', fontWeight: 400 }}>Conquistas da Semana</span>
                  <MiniTetrisPiece color="green" delay={0.9} />
                </div>
                <div className="h-px flex-1 bg-gradient-to-l from-transparent to-purple-500/50" />
              </div>

              {/* Achievement items */}
              <div className="space-y-2">
                {achievements.slice(0, 3).map((achievement, index) => (
                  <AchievementBlock key={achievement} achievement={achievement} index={index} />
                ))}
              </div>
            </motion.div>
          </div>

          {/* Subtle corner accents */}
          <div className="absolute top-4 left-4 w-8 h-8 border-l-2 border-t-2 border-purple-500/30 rounded-tl-lg" />
          <div className="absolute top-4 right-4 w-8 h-8 border-r-2 border-t-2 border-purple-500/30 rounded-tr-lg" />
          <div className="absolute bottom-4 left-4 w-8 h-8 border-l-2 border-b-2 border-purple-500/30 rounded-bl-lg" />
          <div className="absolute bottom-4 right-4 w-8 h-8 border-r-2 border-b-2 border-purple-500/30 rounded-br-lg" />

          {/* Subtle border glow */}
          <div className="absolute inset-0 rounded-3xl border border-purple-500/20 pointer-events-none" />
        </motion.div>
      </div>

      {/* Footer actions */}
      <motion.footer
        className="p-6 pb-8 text-center space-y-4"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 1.1 }}
      >
        <ProgressButton
          variant="cta"
          size="lg"
          className="w-full max-w-sm mx-auto bg-gradient-to-r from-purple-600 to-purple-700 hover:from-purple-500 hover:to-purple-600 shadow-lg shadow-purple-500/30"
          onClick={handleDownload}
        >
          <Download className="w-4 h-4 mr-2" />
          Salvar imagem
        </ProgressButton>
        <p className="text-xs" style={{ color: '#fff', fontWeight: 400 }}>Compartilhe no Instagram Stories</p>
      </motion.footer>
    </div>
  );
}

export default Share;
